import { createContext, useContext, useState, useEffect, useRef } from "react";
import { AnswersContext } from "./AnswersContext";
import { EmailTypeContext } from "./EmailTypeContext";
import { v4 as uuidv4 } from "uuid";
import clearStorage from "../utils/clearStorage";
import confirmLanguage from "../utils/confirmLanguage";

export const DataContext = createContext(null);

export const DataProvider = ({ children }) => {
  const [surveyMetaData, setSurveyMetaData] = useState();
  const [surveyData, setSurveyData] = useState();
  const [paginatedData, setPaginatedData] = useState([]);
  const [previousAnswers, setPreviousAnswers] = useState([]);
  const {
    setEmailTypeQuestions,
    setFloatingTypeQuestions,
    setNumberTypeQuestions,
  } = useContext(EmailTypeContext);
  const [languageArray, setLanguageArray] = useState([]);
  const [defaultLanguage, setDefaultLanguage] = useState();
  const [englishCode, setEnglishCode] = useState();
  const [chosenLanguage, setChosenLanguage] = useState(null);
  const [fetchError, setFetchError] = useState(null);
  const [messageError, setMessageError] = useState(null);
  const [attention, setAttention] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [thankYou, setThankYou] = useState(false);
  const [stateCleanser, setStateCleanser] = useState(false);
  // const [wrapperWidth, setWrapperWidth] = useState(768);
  const [wrapperWidth, setWrapperWidth] = useState(
    window.innerWidth >= 971 ? "962px" : `${window.innerWidth - 10}px`
  );
  const { setCurrentPageAnswers, answeredQuestions, setAnsweredQuestions } =
    useContext(AnswersContext);

  const handleResize = () => {
    if (window.innerWidth < 971) {
      setWrapperWidth(`${window.innerWidth - 10}px`);
    }
  };

  useEffect(() => {
    window.addEventListener("resize", handleResize, false);
  }, []);

  const loaderRef = useRef(null);

  useEffect(() => {
    if (chosenLanguage && surveyMetaData) {
      let parsedChosenLanguage = Number.parseInt(chosenLanguage);

      paginateQuestions(
        surveyMetaData.find(
          (metadata) => metadata.ProjectId === parsedChosenLanguage
        )?.QuestionModel
      );
    }
  }, [chosenLanguage, surveyMetaData]);

  let paginatedDataCopy = [[]];

  const generateLanguageArray = (data) => {
    setLanguageArray(
      Object.keys(data).map((language) => {
        return {
          pid: `${data[language].ProjectId}`,
          language: data[language].Language,
        };
      })
    );
  };

  const paginateQuestions = (data) => {
    let startingCount = 1;

    data?.forEach((question) => {
      if (Number.parseInt(question.PageNo) === startingCount) {
        paginatedDataCopy[startingCount - 1].push(question);
      } else {
        paginatedDataCopy.push([question]);
        startingCount++;
      }
    });

    let emailTypeQuestionsCopy = {};
    let numberTypeQuestionsCopy = {};
    let floatTypeQuestionsCopy = {};

    paginatedDataCopy.forEach((page) => {
      page.forEach((question) =>
        question.Answers.forEach((answer) => {
          if (answer.AnswerType === "Email") {
            emailTypeQuestionsCopy[
              `${answer.Expression.split("=")[0]}_${
                answer.Expression.split("=")[1]
              }`
            ] = question.QuestionCode;
          }
        })
      );
    });

    paginatedDataCopy.forEach((page) => {
      page.forEach((question) =>
        question.Answers.forEach((answer) => {
          if (answer.AnswerType === "Numeric") {
            numberTypeQuestionsCopy[
              `${answer.Expression.split("=")[0]}_${
                answer.Expression.split("=")[1]
              }`
            ] = question.QuestionCode;
          }
        })
      );
    });

    paginatedDataCopy.forEach((page) => {
      page.forEach((question) =>
        question.Answers.forEach((answer) => {
          if (answer.AnswerType === "FloatingPoint") {
            floatTypeQuestionsCopy[
              `${answer.Expression.split("=")[0]}_${
                answer.Expression.split("=")[1]
              }`
            ] = question.QuestionCode;
          }
        })
      );
    });

    if (answeredQuestions.length === 0) {
      let blankArray = [];
      paginatedDataCopy.forEach((arr) => blankArray.push([]));
      setAnsweredQuestions(blankArray);
    }

    setEmailTypeQuestions(emailTypeQuestionsCopy);
    setNumberTypeQuestions(numberTypeQuestionsCopy);
    setFloatingTypeQuestions(floatTypeQuestionsCopy);

    setPaginatedData(paginatedDataCopy);
  };

  useEffect(() => {
    let currentUrl = window.location.href;

    if (sessionStorage.getItem("uid") && !currentUrl.includes("uid")) {
      currentUrl = currentUrl.includes("?")
        ? `${currentUrl}&uid=${sessionStorage.getItem("uid")}`
        : `${currentUrl}?uid=${sessionStorage.getItem("uid")}`;
      window.history.pushState({}, "", currentUrl);
    }

    if (sessionStorage.getItem("pid") && !currentUrl.includes("pid")) {
      currentUrl = currentUrl.includes("?")
        ? `${currentUrl.replace(/&pid=\d+/g,'').replace(/pid=\d+/g,'')}&pid=${sessionStorage.getItem("pid")}`
        : `${currentUrl}?uid=${sessionStorage.getItem("pid")}`;
      window.history.pushState({}, "", currentUrl);
    }

    //pulling data from query params or setting query params
    if (currentUrl.includes("sid")) {
      sessionStorage.setItem(
        "sid",
        window.location.href.split("sid=")[1].split("&")[0]
      );
      sessionStorage.setItem(
        "vid",
        currentUrl.includes("vid") ? window.location.href.split("vid=")[1].split("&")[0] : "null"
      );

      if (currentUrl.includes("uid")) {
        sessionStorage.setItem(
          "uid",
          window.location.href.split("uid=")[1].split("&")[0]
        );
      }

      if (
        !sessionStorage.getItem("uid") ||
        sessionStorage.getItem("uid") === "undefined"
      ) {
        let newUid = uuidv4();
        sessionStorage.setItem("uid", newUid);
        window.history.pushState(
          {},
          "",
          currentUrl.includes("?")
            ? `${currentUrl}&uid=${newUid}`
            : `${currentUrl}?uid=${newUid}`
        );
      } else if (sessionStorage.getItem("uid") && !currentUrl.includes("uid")) {
        window.history.pushState(
          {},
          "",
          currentUrl.includes("?")
            ? `${currentUrl}&uid=${sessionStorage.getItem("uid")}`
            : `${currentUrl}?uid=${sessionStorage.getItem("uid")}`
        );
      }

      loaderRef?.current?.continuousStart();

      let fetchData = () => {
                    
        fetch(
          `https://surveyapp.onrcx.net/${window.location.href.split(/sid=/)[1].split('&')[0]}/api/SurveyObjectJson`, 
          // `http://test.onrcx.com/${window.location.href.split(/sid=/)[1].split('&')[0]}/api/SurveyObjectJson`, 
          {
            method: "POST",
            headers: {
              Accept: "application/json",
              "Content-Type": "application/json",
            },
            body: JSON.stringify({
              ProjectCode: sessionStorage.getItem("sid"),
              UserScreen: `${
                window.innerWidth > 0 ? window.innerWidth : window.screen.width
              }x${
                window.innerHeight > 0
                  ? window.innerHeight
                  : window.screen.height
              }`,
              Version: sessionStorage.getItem("vid"),
              UserId: `${sessionStorage.getItem("uid")}`,
              TabId: `${sessionStorage
                .getItem("tidInitial")
                ?.replace("Z", "")}`,
              UserAgent: `${navigator.userAgent}`,
              QueryParams: `${new URLSearchParams(window.location.search).toString()}`,
              ProjectId: `${currentUrl.includes('pid') ? window.location.href.split("pid=")[1].split("&")[0] : currentUrl.includes('dl') ? window.location.href.split("dl=")[1].split("&")[0] : -998}`
            }),
          }
        )
          .then((res) => res.json())
          .then((data) => {
            if (data && data.Error) {
              setMessageError(
                data.Error.replace(/< br \/>/g, " ")
                  .replace(/<br \/>/g, " ")
                  .replace(/<br\/>/g, " ")
              );
              setAttention(data?.Attention);
              clearStorage();
              loaderRef.current.complete();
            } else if (data && data.ThankYou) {
                 
              if (data?.Redirect && data.Redirect !== "null") {
                window.location.href = data.Redirect;
              } else {
                   
                setThankYou(data.ThankYou)
              }
            } else if (data) {

              setDefaultLanguage(`${data[" English"].ProjectId}`);
              setEnglishCode(`${data[" English"].ProjectId}`);
              sessionStorage.setItem("tid", sessionStorage.tidInitial);
              sessionStorage.removeItem("tidInitial");

              //remove AnswersSelected and store it in a context variable
              const notAllowed = ["AnswersSelected"];

              const filteredData = Object.fromEntries(
                Object.entries(data).filter(
                  ([key, val]) => !notAllowed.includes(key)
                )
              );

              //open with dropdown
              // filteredData[" English"].QuestionModel = [
              //   {
              //     "QuestionNo": 1,
              //     "QuestionCode": "q_1",
              //     "QuestionType": "Single",
              //     "QuestionId": 49119,
              //     "MatrixQuestionContext": null,
              //     "QuestionHead": null,
              //     "QuestionName": "<span class=\"text-bold\">In which country are you located?</span>",
              //     "MinAnswer": 1,
              //     "MaxAnswer": 1,
              //     "Answers": [
              //         {
              //             "AnswerId": 398936,
              //             "AnswerCode": "A2",
              //             "AnswerOrder": 1,
              //             "AnswerLabel": "China",
              //             "AnswerType": "Normal",
              //             "AnswerLength": 0,
              //             "Expression": "A2=1",
              //             "showIf": null,
              //             "hideIf": null,
              //             "groupName": "Header"
              //         },
              //         {
              //             "AnswerId": 398937,
              //             "AnswerCode": "A2",
              //             "AnswerOrder": 2,
              //             "AnswerLabel": "Beijing",
              //             "AnswerType": "Normal",
              //             "AnswerLength": 0,
              //             "Expression": "A2=2",
              //             "showIf": null,
              //             "hideIf": null,
              //             "groupName": "China"
              //         },
              //         {
              //             "AnswerId": 398938,
              //             "AnswerCode": "A2",
              //             "AnswerOrder": 3,
              //             "AnswerLabel": "Shanghai",
              //             "AnswerType": "Normal",
              //             "AnswerLength": 0,
              //             "Expression": "A2=3",
              //             "showIf": null,
              //             "hideIf": null,
              //             "groupName": "China"
              //         },
              //         {
              //             "AnswerId": 398939,
              //             "AnswerCode": "A2",
              //             "AnswerOrder": 4,
              //             "AnswerLabel": "Shenzhen",
              //             "AnswerType": "Normal",
              //             "AnswerLength": 0,
              //             "Expression": "A2=4",
              //             "showIf": null,
              //             "hideIf": null,
              //             "groupName": "China"
              //         },
              //         {
              //             "AnswerId": 398940,
              //             "AnswerCode": "A2",
              //             "AnswerOrder": 5,
              //             "AnswerLabel": "Xian",
              //             "AnswerType": "Normal",
              //             "AnswerLength": 0,
              //             "Expression": "A2=5",
              //             "showIf": null,
              //             "hideIf": null,
              //             "groupName": "China"
              //         },
              //         {
              //             "AnswerId": 398941,
              //             "AnswerCode": "A2",
              //             "AnswerOrder": 6,
              //             "AnswerLabel": "Other",
              //             "AnswerType": "Normal",
              //             "AnswerLength": 0,
              //             "Expression": "A2=6",
              //             "showIf": null,
              //             "hideIf": null,
              //             "groupName": "China"
              //         },
              //         {
              //             "AnswerId": 398942,
              //             "AnswerCode": "A2",
              //             "AnswerOrder": 7,
              //             "AnswerLabel": "Germany",
              //             "AnswerType": "Normal",
              //             "AnswerLength": 0,
              //             "Expression": "A2=7",
              //             "showIf": null,
              //             "hideIf": null,
              //             "groupName": null
              //         },
              //         {
              //             "AnswerId": 398943,
              //             "AnswerCode": "A2",
              //             "AnswerOrder": 8,
              //             "AnswerLabel": "India",
              //             "AnswerType": "Normal",
              //             "AnswerLength": 0,
              //             "Expression": "A2=8",
              //             "showIf": null,
              //             "hideIf": null,
              //             "groupName": null
              //         },
              //         {
              //             "AnswerId": 398944,
              //             "AnswerCode": "A2",
              //             "AnswerOrder": 9,
              //             "AnswerLabel": "Japan",
              //             "AnswerType": "Normal",
              //             "AnswerLength": 0,
              //             "Expression": "A2=9",
              //             "showIf": null,
              //             "hideIf": null,
              //             "groupName": null
              //         },
              //         {
              //             "AnswerId": 398945,
              //             "AnswerCode": "A2",
              //             "AnswerOrder": 10,
              //             "AnswerLabel": "Singapore",
              //             "AnswerType": "Normal",
              //             "AnswerLength": 0,
              //             "Expression": "A2=10",
              //             "showIf": null,
              //             "hideIf": null,
              //             "groupName": null
              //         },
              //         {
              //             "AnswerId": 398946,
              //             "AnswerCode": "A2",
              //             "AnswerOrder": 11,
              //             "AnswerLabel": "South Korea",
              //             "AnswerType": "Normal",
              //             "AnswerLength": 0,
              //             "Expression": "A2=11",
              //             "showIf": null,
              //             "hideIf": null,
              //             "groupName": null
              //         },
              //         {
              //             "AnswerId": 398947,
              //             "AnswerCode": "A2",
              //             "AnswerOrder": 12,
              //             "AnswerLabel": "Taiwan",
              //             "AnswerType": "Normal",
              //             "AnswerLength": 0,
              //             "Expression": "A2=12",
              //             "showIf": null,
              //             "hideIf": null,
              //             "groupName": null
              //         },
              //         {
              //             "AnswerId": 398948,
              //             "AnswerCode": "A2",
              //             "AnswerOrder": 13,
              //             "AnswerLabel": "USA (United States of America)",
              //             "AnswerType": "Normal",
              //             "AnswerLength": 0,
              //             "Expression": "A2=13",
              //             "showIf": null,
              //             "hideIf": null,
              //             "groupName": null
              //         },
              //         {
              //             "AnswerId": 398949,
              //             "AnswerCode": "A2",
              //             "AnswerOrder": 14,
              //             "AnswerLabel": "Malaysia",
              //             "AnswerType": "Normal",
              //             "AnswerLength": 0,
              //             "Expression": "A2=14",
              //             "showIf": null,
              //             "hideIf": null,
              //             "groupName": null
              //         },
              //         {
              //             "AnswerId": 398950,
              //             "AnswerCode": "A2",
              //             "AnswerOrder": 15,
              //             "AnswerLabel": "Other",
              //             "AnswerType": "Normal",
              //             "AnswerLength": 0,
              //             "Expression": "A2=15",
              //             "showIf": null,
              //             "hideIf": null,
              //             "groupName": null
              //         }
              //     ],
              //     "showIf": null,
              //     "hideIf": null,
              //     "PageNo": 1,
              //     "QuestionNote": null
              //   }
              // ]

              //slider
              // filteredData[" English"].QuestionModel = [
              //   {
              //     QuestionNo: 1,
              //     QuestionCode: "Q32",
              //     // QuestionType: "Rating",
              //     QuestionType: "Range",
              //     MaxAnswer: 25,
              //     MinAnswer: 0,
              //     // Interval: 1,
              //     Interval: 0.25,
              //     QuestionId: 10355,
              //     QuestionHead:
              //       "Thank you for your feedback so far. This is the last page.",
              //     QuestionName:
              //       '<span class="text-bold">How likely is it that you would recommend Intel to a friend or colleague?</span>',
              //     Answers: [
              //       {
              //         AnswerId: 64540,
              //         AnswerCode: "A32",
              //         AnswerOrder: 1,
              //         AnswerLabel: "0 - Not at All Likely",
              //         AnswerType: "Normal",
              //         AnswerLength: 0,
              //         Expression: "A32=1",
              //         showIf: null,
              //         hideIf: null,
              //       },
              //       {
              //         AnswerId: 64541,
              //         AnswerCode: "A32",
              //         AnswerOrder: 2,
              //         AnswerLabel: "1",
              //         AnswerType: "Normal",
              //         AnswerLength: 0,
              //         Expression: "A32=2",
              //         showIf: null,
              //         hideIf: null,
              //       },
              //       {
              //         AnswerId: 64542,
              //         AnswerCode: "A32",
              //         AnswerOrder: 3,
              //         AnswerLabel: "2",
              //         AnswerType: "Normal",
              //         AnswerLength: 0,
              //         Expression: "A32=3",
              //         showIf: null,
              //         hideIf: null,
              //       },
              //       {
              //         AnswerId: 64543,
              //         AnswerCode: "A32",
              //         AnswerOrder: 4,
              //         AnswerLabel: "3",
              //         AnswerType: "Normal",
              //         AnswerLength: 0,
              //         Expression: "A32=4",
              //         showIf: null,
              //         hideIf: null,
              //       },
              //       {
              //         AnswerId: 64544,
              //         AnswerCode: "A32",
              //         AnswerOrder: 5,
              //         AnswerLabel: "4",
              //         AnswerType: "Normal",
              //         AnswerLength: 0,
              //         Expression: "A32=5",
              //         showIf: null,
              //         hideIf: null,
              //       },
              //       {
              //         AnswerId: 64545,
              //         AnswerCode: "A32",
              //         AnswerOrder: 6,
              //         AnswerLabel: "5",
              //         AnswerType: "Normal",
              //         AnswerLength: 0,
              //         Expression: "A32=6",
              //         showIf: null,
              //         hideIf: null,
              //       },
              //       {
              //         AnswerId: 64546,
              //         AnswerCode: "A32",
              //         AnswerOrder: 7,
              //         AnswerLabel: "6",
              //         AnswerType: "Normal",
              //         AnswerLength: 0,
              //         Expression: "A32=7",
              //         showIf: null,
              //         hideIf: null,
              //       },
              //       {
              //         AnswerId: 64547,
              //         AnswerCode: "A32",
              //         AnswerOrder: 8,
              //         AnswerLabel: "7",
              //         AnswerType: "Normal",
              //         AnswerLength: 0,
              //         Expression: "A32=8",
              //         showIf: null,
              //         hideIf: null,
              //       },
              //       {
              //         AnswerId: 64548,
              //         AnswerCode: "A32",
              //         AnswerOrder: 9,
              //         AnswerLabel: "8",
              //         AnswerType: "Normal",
              //         AnswerLength: 0,
              //         Expression: "A32=9",
              //         showIf: null,
              //         hideIf: null,
              //       },
              //       {
              //         AnswerId: 64549,
              //         AnswerCode: "A32",
              //         AnswerOrder: 10,
              //         AnswerLabel: "9",
              //         AnswerType: "Normal",
              //         AnswerLength: 0,
              //         Expression: "A32=10",
              //         showIf: null,
              //         hideIf: null,
              //       },
              //       {
              //         AnswerId: 64550,
              //         AnswerCode: "A32",
              //         AnswerOrder: 11,
              //         AnswerLabel: "10 - Extremely Likely",
              //         AnswerType: "Normal",
              //         AnswerLength: 0,
              //         Expression: "A32=11",
              //         showIf: null,
              //         hideIf: null,
              //       },
              //     ],
              //     showIf: null,
              //     hideIf: null,
              //     PageNo: 1,
              //     QuestionNote: null,
              //   },
              // ];

              //matrix label
              // filteredData[" English"].QuestionModel = [
              //   {
              //     QuestionNo: 1,
              //     QuestionCode: "q_1",
              //     QuestionType: "MatrixLabel",
              //     QuestionId: 47915,
              //     QuestionHead:
              //       "Please rate your agreement with ABC restaurant:",
              //     QuestionName: "The food is very delicious",
              //     MinAnswer: 1,
              //     MaxAnswer: 1,
              //     Answers: [
              //       {
              //         AnswerId: 388763,
              //         AnswerCode: "A1_1",
              //         AnswerOrder: 1,
              //         AnswerLabel: "Strongly disagree",
              //         AnswerType: "Normal",
              //         AnswerLength: 0,
              //         Expression: "A1_1=1",
              //         showIf: null,
              //         hideIf: null,
              //       },
              //       {
              //         AnswerId: 388763,
              //         AnswerCode: "A1_1",
              //         AnswerOrder: 2,
              //         AnswerLabel: "Disagree",
              //         AnswerType: "Normal",
              //         AnswerLength: 0,
              //         Expression: "A1_1=2",
              //         showIf: null,
              //         hideIf: null,
              //       },
              //       {
              //         AnswerId: 388763,
              //         AnswerCode: "A1_1",
              //         AnswerOrder: 3,
              //         AnswerLabel: "Neutral",
              //         AnswerType: "Normal",
              //         AnswerLength: 0,
              //         Expression: "A1_1=3",
              //         showIf: null,
              //         hideIf: null,
              //       },
              //       {
              //         AnswerId: 388763,
              //         AnswerCode: "A1_1",
              //         AnswerOrder: 4,
              //         AnswerLabel: "Agree",
              //         AnswerType: "Normal",
              //         AnswerLength: 0,
              //         Expression: "A1_1=4",
              //         showIf: null,
              //         hideIf: null,
              //       },
              //       {
              //         AnswerId: 388763,
              //         AnswerCode: "A1_1",
              //         AnswerOrder: 5,
              //         AnswerLabel: "Strongly agree",
              //         AnswerType: "Normal",
              //         AnswerLength: 0,
              //         Expression: "A1_1=5",
              //         showIf: null,
              //         hideIf: null,
              //       },
              //     ],
              //     showIf: null,
              //     hideIf: null,
              //     PageNo: 1,
              //     QuestionNote: null,
              //   },
              //   {
              //     QuestionNo: 2,
              //     QuestionCode: "q_2",
              //     QuestionType: "MatrixLabel",
              //     QuestionId: 47915,
              //     QuestionHead:
              //       "Please rate your agreement with ABC restaurant:",
              //     QuestionName: "The service is supper good",
              //     MinAnswer: 1,
              //     MaxAnswer: 1,
              //     Answers: [
              //       {
              //         AnswerId: 388764,
              //         AnswerCode: "A2_2",
              //         AnswerOrder: 1,
              //         AnswerLabel: "Strongly disagree",
              //         AnswerType: "Normal",
              //         AnswerLength: 0,
              //         Expression: "A2_2=1",
              //         showIf: null,
              //         hideIf: null,
              //       },
              //       {
              //         AnswerId: 388764,
              //         AnswerCode: "A2_2",
              //         AnswerOrder: 2,
              //         AnswerLabel: "Disagree",
              //         AnswerType: "Normal",
              //         AnswerLength: 0,
              //         Expression: "A2_2=2",
              //         showIf: null,
              //         hideIf: null,
              //       },
              //       {
              //         AnswerId: 388764,
              //         AnswerCode: "A2_2",
              //         AnswerOrder: 3,
              //         AnswerLabel: "Neutral",
              //         AnswerType: "Normal",
              //         AnswerLength: 0,
              //         Expression: "A2_2=3",
              //         showIf: null,
              //         hideIf: null,
              //       },
              //       {
              //         AnswerId: 388764,
              //         AnswerCode: "A2_2",
              //         AnswerOrder: 4,
              //         AnswerLabel: "Agree",
              //         AnswerType: "Normal",
              //         AnswerLength: 0,
              //         Expression: "A2_2=4",
              //         showIf: null,
              //         hideIf: null,
              //       },
              //       {
              //         AnswerId: 388764,
              //         AnswerCode: "A2_2",
              //         AnswerOrder: 5,
              //         AnswerLabel: "Strongly agree",
              //         AnswerType: "Normal",
              //         AnswerLength: 0,
              //         Expression: "A2_2=5",
              //         showIf: null,
              //         hideIf: null,
              //       },
              //     ],
              //     showIf: null,
              //     hideIf: null,
              //     PageNo: 1,
              //     QuestionNote: null,
              //   },
              // ];

              //multiple
              // filteredData[" English"].QuestionModel = [
              //   {
              //     QuestionNo: 3,
              //     QuestionCode: "q_3",
              //     QuestionType: "Multiple",
              //     QuestionId: 47916,
              //     QuestionHead: null,
              //     QuestionName: "What are you doing this year for Christmas?",
              //     MinAnswer: 1,
              //     MaxAnswer: 1,
              //     Answers: [
              //       {
              //         AnswerId: 388765,
              //         AnswerCode: "A3_1",
              //         AnswerOrder: 1,
              //         AnswerLabel: "Spending time with friends and family",
              //         AnswerType: "Normal",
              //         AnswerLength: 0,
              //         Expression: "A3_1=1",
              //         showIf: null,
              //         hideIf: null,
              //       },
              //       {
              //         AnswerId: 388766,
              //         AnswerCode: "A3_2",
              //         AnswerOrder: 1,
              //         AnswerLabel: "Going on vacation somewhere warm",
              //         AnswerType: "Normal",
              //         AnswerLength: 0,
              //         Expression: "A3_2=1",
              //         showIf: null,
              //         hideIf: null,
              //       },
              //       {
              //         AnswerId: 388767,
              //         AnswerCode: "A3_3",
              //         AnswerOrder: 1,
              //         AnswerLabel: "I’ll be home alone",
              //         AnswerType: "Normal",
              //         AnswerLength: 0,
              //         Expression: "A3_3=1",
              //         showIf: null,
              //         hideIf: null,
              //       },
              //       {
              //         AnswerId: 398933,
              //         AnswerCode: "A3_4",
              //         AnswerOrder: 1,
              //         AnswerLabel: "None of the above",
              //         AnswerType: "None",
              //         AnswerLength: 0,
              //         Expression: "A3_4=1",
              //         showIf: null,
              //         hideIf: null,
              //       },
              //     ],
              //     showIf: null,
              //     hideIf: null,
              //     PageNo: 1,
              //     QuestionNote: null,
              //   },
              // ];

              // //rank label
              // filteredData[" English"].QuestionModel = [
              //   {
              //     QuestionNo: 3,
              //     QuestionCode: "q_3",
              //     QuestionType: "Multiple",
              //     QuestionId: 47916,
              //     QuestionNo: 1,
              //     QuestionCode: "q_1",
              //     QuestionType: "Rank",
              //     QuestionId: 47914,
              //     QuestionHead: null,
              //     QuestionName: "What are you doing this year for Christmas?",
              //     QuestionName:
              //       "What is your most preferred date for the Christmas activity?",
              //     MinAnswer: 1,
              //     MaxAnswer: 1,
              //     Answers: [
              //       {
              //         AnswerId: 388765,
              //         AnswerCode: "A3_1",
              //         AnswerId: 388758,
              //         AnswerCode: "A1_1",
              //         AnswerOrder: 1,
              //         AnswerLabel: "Spending time with friends and family",
              //         AnswerLabel: "Monday December 16<sup>th</sup>, 2023",
              //         AnswerType: "Normal",
              //         AnswerLength: 0,
              //         Expression: "A3_1=1",
              //         Expression: "A1_1=$Input$",
              //         showIf: null,
              //         hideIf: null,
              //       },
              //       {
              //         AnswerId: 388766,
              //         AnswerCode: "A3_2",
              //         AnswerOrder: 1,
              //         AnswerLabel: "Going on vacation somewhere warm",
              //         AnswerId: 388759,
              //         AnswerCode: "A1_2",
              //         AnswerOrder: 2,
              //         AnswerLabel: "Tuesday December 17<sup>th</sup>, 2023",
              //         AnswerType: "Normal",
              //         AnswerLength: 0,
              //         Expression: "A3_2=1",
              //         Expression: "A1_2=$Input$",
              //         showIf: null,
              //         hideIf: null,
              //       },
              //       {
              //         AnswerId: 388767,
              //         AnswerCode: "A3_3",
              //         AnswerOrder: 1,
              //         AnswerLabel: "I’ll be home alone",
              //         AnswerId: 388760,
              //         AnswerCode: "A1_3",
              //         AnswerOrder: 3,
              //         AnswerLabel: "Thursday December 19<sup>th</sup>, 2023",
              //         AnswerType: "Normal",
              //         AnswerLength: 0,
              //         Expression: "A3_3=1",
              //         Expression: "A1_3=$Input$",
              //         showIf: null,
              //         hideIf: null,
              //       },
              //       {
              //         AnswerId: 398933,
              //         AnswerCode: "A3_4",
              //         AnswerOrder: 1,
              //         AnswerLabel: "None of the above",
              //         AnswerId: 388761,
              //         AnswerCode: "A1_4_o",
              //         AnswerOrder: 4,
              //         AnswerLabel: "Other (Please Specify):",
              //         AnswerType: "Other",
              //         AnswerLength: 150,
              //         Expression: "A1_4_o=$Input$",
              //         showIf: null,
              //         hideIf: null,
              //       },
              //       {
              //         AnswerId: 388762,
              //         AnswerCode: "A1_5",
              //         AnswerOrder: 5,
              //         AnswerLabel: "No Preference",
              //         AnswerType: "None",
              //         AnswerLength: 0,
              //         Expression: "A3_4=1",
              //         Expression: "A1_5=$Input$",
              //         showIf: null,
              //         hideIf: null,
              //       },
              //     ],
              //     showIf: null,
              //     hideIf: null,
              //     PageNo: 1,
              //     QuestionNote: null,
              //   },
              // ];

              // //percentage label
              // filteredData[" English"].QuestionModel = [
              //   {
              //     QuestionNo: 3,
              //     QuestionCode: "q_3",
              //     QuestionType: "Multiple",
              //     QuestionId: 47916,
              //     QuestionNo: 7,
              //     QuestionCode: "q_7",
              //     QuestionType: "Percentage",
              //     QuestionId: 49114,
              //     QuestionHead: null,
              //     QuestionName: "What are you doing this year for Christmas?",
              //     QuestionName:
              //       '<span class="text-bold">Please let us know the percentage of time you spent on each of the following in a single day.</span>',
              //     MinAnswer: 1,
              //     MaxAnswer: 1,
              //     Answers: [
              //       {
              //         AnswerId: 388765,
              //         AnswerCode: "A3_1",
              //         AnswerId: 398907,
              //         AnswerCode: "A7_1",
              //         AnswerOrder: 1,
              //         AnswerLabel: "Spending time with friends and family",
              //         AnswerLabel: "Eat",
              //         AnswerType: "Normal",
              //         AnswerLength: 0,
              //         Expression: "A3_1=1",
              //         Expression: "A7_1=$Input$",
              //         showIf: null,
              //         hideIf: null,
              //       },
              //       {
              //         AnswerId: 388766,
              //         AnswerCode: "A3_2",
              //         AnswerOrder: 1,
              //         AnswerLabel: "Going on vacation somewhere warm",
              //         AnswerId: 398908,
              //         AnswerCode: "A7_2",
              //         AnswerOrder: 2,
              //         AnswerLabel: "Sleep",
              //         AnswerType: "Normal",
              //         AnswerLength: 0,
              //         Expression: "A3_2=1",
              //         Expression: "A7_2=$Input$",
              //         showIf: null,
              //         hideIf: null,
              //       },
              //       {
              //         AnswerId: 388767,
              //         AnswerCode: "A3_3",
              //         AnswerOrder: 1,
              //         AnswerLabel: "I’ll be home alone",
              //         AnswerId: 398909,
              //         AnswerCode: "A7_3",
              //         AnswerOrder: 3,
              //         AnswerLabel: "Work",
              //         AnswerType: "Normal",
              //         AnswerLength: 0,
              //         Expression: "A3_3=1",
              //         Expression: "A7_3=$Input$",
              //         showIf: null,
              //         hideIf: null,
              //       },
              //       {
              //         AnswerId: 398933,
              //         AnswerCode: "A3_4",
              //         AnswerOrder: 1,
              //         AnswerLabel: "None of the above",
              //         AnswerType: "None",
              //         AnswerId: 398910,
              //         AnswerCode: "A7_4",
              //         AnswerOrder: 4,
              //         AnswerLabel: "Play",
              //         AnswerType: "Normal",
              //         AnswerLength: 0,
              //         Expression: "A7_4=$Input$",
              //         showIf: null,
              //         hideIf: null,
              //       },
              //       {
              //         AnswerId: 398911,
              //         AnswerCode: "A7_5_o",
              //         AnswerOrder: 5,
              //         AnswerLabel: "Other (Please Specify):",
              //         AnswerType: "Other",
              //         AnswerLength: 100,
              //         Expression: "A7_5_o=$Input$",
              //         showIf: null,
              //         hideIf: null,
              //       },
              //       {
              //         AnswerId: 398932,
              //         AnswerCode: "A7_6",
              //         AnswerOrder: 6,
              //         AnswerLabel: "Total: <-qDyn-> %",
              //         AnswerType: "Stay",
              //         AnswerLength: 0,
              //         Expression: "A3_4=1",
              //         Expression: "A7_6=$Input$",
              //         showIf: null,
              //         hideIf: null,
              //       },
              //     ],
              //     showIf: null,
              //     hideIf: null,
              //     PageNo: 1,
              //     QuestionNote: null,
              //   },
              // ];

              //dropdown
              // filteredData[" English"].QuestionModel = [
              //   {
              //     "QuestionNo": 8,
              //     "QuestionCode": "q_8",
              //     "QuestionType": "DropDown",
              //     "QuestionId": 49115,
              //     "MatrixQuestionContext": null,
              //     "QuestionHead": "<span class=\"text-bold\">What type of sport you most enjoyed?</span>",
              //     "QuestionName": null,
              //     "MinAnswer": 2,
              //     "MaxAnswer": 2,
              //     "Answers": [
              //       {
              //         "AnswerId": 398913,
              //         "AnswerCode": "A8_1",
              //         "AnswerOrder": 1,
              //         "AnswerLabel": "Aquatic and paddle sports",
              //         "AnswerType": "Normal",
              //         "AnswerLength": 0,
              //         "Expression": "A8_1=1",
              //         "showIf": null,
              //         "hideIf": null
              //       },
              //       {
              //         "AnswerId": 398914,
              //         "AnswerCode": "A8_1",
              //         "AnswerOrder": 2,
              //         "AnswerLabel": "Ball games",
              //         "AnswerType": "Normal",
              //         "AnswerLength": 0,
              //         "Expression": "A8_1=2",
              //         "showIf": null,
              //         "hideIf": null
              //       },
              //       {
              //         "AnswerId": 398915,
              //         "AnswerCode": "A8_1",
              //         "AnswerOrder": 3,
              //         "AnswerLabel": "Racket (or racquet) sports",
              //         "AnswerType": "Normal",
              //         "AnswerLength": 0,
              //         "Expression": "A8_1=3",
              //         "showIf": null,
              //         "hideIf": null
              //       },
              //       {
              //         "AnswerId": 398916,
              //         "AnswerCode": "A8_1",
              //         "AnswerOrder": 4,
              //         "AnswerLabel": "Other",
              //         "AnswerType": "Normal",
              //         "AnswerLength": 0,
              //         "Expression": "A8_1=4",
              //         "showIf": null,
              //         "hideIf": null
              //       }
              //     ],
              //     "showIf": null,
              //     "hideIf": null,
              //     "PageNo": 1,
              //     "QuestionNote": null
              //   },
              //   {
              //     "QuestionNo": 8,
              //     "QuestionCode": "q_8",
              //     "QuestionType": "DropDown",
              //     "QuestionId": 49115,
              //     "MatrixQuestionContext": null,
              //     "QuestionHead": "<span class=\"text-bold\">What type of sport you most enjoyed?</span>",
              //     "QuestionName": null,
              //     "MinAnswer": 2,
              //     "MaxAnswer": 2,
              //     "Answers": [
              //       {
              //         "AnswerId": 398918,
              //         "AnswerCode": "A8_2",
              //         "AnswerOrder": 1,
              //         "AnswerLabel": "Canoeing",
              //         "AnswerType": "Normal",
              //         "AnswerLength": 0,
              //         "Expression": "A8_2=1",
              //         "showIf": "(<A8_1=1>) ",
              //         "hideIf": null
              //       },
              //       {
              //         "AnswerId": 398919,
              //         "AnswerCode": "A8_2",
              //         "AnswerOrder": 2,
              //         "AnswerLabel": "Kayaking",
              //         "AnswerType": "Normal",
              //         "AnswerLength": 0,
              //         "Expression": "A8_2=2",
              //         "showIf": "(<A8_1=1>) ",
              //         "hideIf": null
              //       },
              //       {
              //         "AnswerId": 398920,
              //         "AnswerCode": "A8_2",
              //         "AnswerOrder": 3,
              //         "AnswerLabel": "Swimming",
              //         "AnswerType": "Normal",
              //         "AnswerLength": 0,
              //         "Expression": "A8_2=3",
              //         "showIf": "(<A8_1=1>) ",
              //         "hideIf": null
              //       },
              //       {
              //         "AnswerId": 398921,
              //         "AnswerCode": "A8_2",
              //         "AnswerOrder": 4,
              //         "AnswerLabel": "Other Aquatic and paddle sports",
              //         "AnswerType": "Normal",
              //         "AnswerLength": 0,
              //         "Expression": "A8_2=4",
              //         "showIf": "(<A8_1=1>) ",
              //         "hideIf": null
              //       },
              //       {
              //         "AnswerId": 398922,
              //         "AnswerCode": "A8_2",
              //         "AnswerOrder": 5,
              //         "AnswerLabel": "Basketball",
              //         "AnswerType": "Normal",
              //         "AnswerLength": 0,
              //         "Expression": "A8_2=5",
              //         "showIf": "(<A8_1=2>) ",
              //         "hideIf": null
              //       },
              //       {
              //         "AnswerId": 398923,
              //         "AnswerCode": "A8_2",
              //         "AnswerOrder": 6,
              //         "AnswerLabel": "Football (or soccer)",
              //         "AnswerType": "Normal",
              //         "AnswerLength": 0,
              //         "Expression": "A8_2=6",
              //         "showIf": "(<A8_1=2>) ",
              //         "hideIf": null
              //       },
              //       {
              //         "AnswerId": 398924,
              //         "AnswerCode": "A8_2",
              //         "AnswerOrder": 7,
              //         "AnswerLabel": "Other Ball games",
              //         "AnswerType": "Normal",
              //         "AnswerLength": 0,
              //         "Expression": "A8_2=7",
              //         "showIf": "(<A8_1=2>) ",
              //         "hideIf": null
              //       },
              //       {
              //         "AnswerId": 398925,
              //         "AnswerCode": "A8_2",
              //         "AnswerOrder": 8,
              //         "AnswerLabel": "Badminton",
              //         "AnswerType": "Normal",
              //         "AnswerLength": 0,
              //         "Expression": "A8_2=8",
              //         "showIf": "(<A8_1=3>) ",
              //         "hideIf": null
              //       },
              //       {
              //         "AnswerId": 398926,
              //         "AnswerCode": "A8_2",
              //         "AnswerOrder": 9,
              //         "AnswerLabel": "Tennis",
              //         "AnswerType": "Normal",
              //         "AnswerLength": 0,
              //         "Expression": "A8_2=9",
              //         "showIf": "(<A8_1=3>) ",
              //         "hideIf": null
              //       },
              //       {
              //         "AnswerId": 398927,
              //         "AnswerCode": "A8_2",
              //         "AnswerOrder": 10,
              //         "AnswerLabel": "Other Racket (or racquet) sports",
              //         "AnswerType": "Normal",
              //         "AnswerLength": 0,
              //         "Expression": "A8_2=10",
              //         "showIf": "(<A8_1=3>) ",
              //         "hideIf": null
              //       },
              //       {
              //         "AnswerId": 398928,
              //         "AnswerCode": "A8_2",
              //         "AnswerOrder": 11,
              //         "AnswerLabel": "Cycling",
              //         "AnswerType": "Normal",
              //         "AnswerLength": 0,
              //         "Expression": "A8_2=11",
              //         "showIf": "(<A8_1=3>) ",
              //         "hideIf": null
              //       },
              //       {
              //         "AnswerId": 398929,
              //         "AnswerCode": "A8_2",
              //         "AnswerOrder": 12,
              //         "AnswerLabel": "Running",
              //         "AnswerType": "Normal",
              //         "AnswerLength": 0,
              //         "Expression": "A8_2=12",
              //         "showIf": "(<A8_1=3>) ",
              //         "hideIf": null
              //       },
              //       {
              //         "AnswerId": 398930,
              //         "AnswerCode": "A8_2",
              //         "AnswerOrder": 13,
              //         "AnswerLabel": "Other sports",
              //         "AnswerType": "Normal",
              //         "AnswerLength": 0,
              //         "Expression": "A8_2=13",
              //         "showIf": "(<A8_1=4>) ",
              //         "hideIf": null
              //       }
              //     ],
              //     "showIf": null,
              //     "hideIf": null,
              //     "PageNo": 1,
              //     "QuestionNote": null
              //   }
              // ]

              //matrix range
              // filteredData[" English"].QuestionModel = [
              //   {
              //     "QuestionNo": 6,
              //     "QuestionCode": "q_6",
              //     "QuestionType": "MatrixRange",
              //     "QuestionId": 49113,
              //     "MatrixQuestionContext": null,
              //     "QuestionHead": "<span class=\"text-bold\">Please rate your level of agreement with the Intel agent:</span>",
              //     "QuestionName": "Professionalism",
              //     "MinAnswer": 4,
              //     "MaxAnswer": 4,
              //     "Answers": [
              //       {
              //         "AnswerId": 398903,
              //         "AnswerCode": "A6_1",
              //         "AnswerOrder": 1,
              //         "AnswerLabel": "Very Dissatisfied",
              //         "AnswerType": "Normal",
              //         "AnswerLength": 0,
              //         "Expression": "A6_1=1",
              //         "showIf": null,
              //         "hideIf": null
              //       },
              //       {
              //         "AnswerId": 0,
              //         "AnswerCode": "A6_1",
              //         "AnswerOrder": 2,
              //         "AnswerLabel": "2",
              //         "AnswerType": "Normal",
              //         "AnswerLength": 0,
              //         "Expression": "A6_1=2",
              //         "showIf": null,
              //         "hideIf": null
              //       },
              //       {
              //         "AnswerId": 0,
              //         "AnswerCode": "A6_1",
              //         "AnswerOrder": 3,
              //         "AnswerLabel": "3",
              //         "AnswerType": "Normal",
              //         "AnswerLength": 0,
              //         "Expression": "A6_1=3",
              //         "showIf": null,
              //         "hideIf": null
              //       },
              //       {
              //         "AnswerId": 398903,
              //         "AnswerCode": "A6_1",
              //         "AnswerOrder": 4,
              //         "AnswerLabel": "Neutral",
              //         "AnswerType": "Normal",
              //         "AnswerLength": 0,
              //         "Expression": "A6_1=4",
              //         "showIf": null,
              //         "hideIf": null
              //       },
              //       {
              //         "AnswerId": 0,
              //         "AnswerCode": "A6_1",
              //         "AnswerOrder": 5,
              //         "AnswerLabel": "5",
              //         "AnswerType": "Normal",
              //         "AnswerLength": 0,
              //         "Expression": "A6_1=5",
              //         "showIf": null,
              //         "hideIf": null
              //       },
              //       {
              //         "AnswerId": 0,
              //         "AnswerCode": "A6_1",
              //         "AnswerOrder": 6,
              //         "AnswerLabel": "6",
              //         "AnswerType": "Normal",
              //         "AnswerLength": 0,
              //         "Expression": "A6_1=6",
              //         "showIf": null,
              //         "hideIf": null
              //       },
              //       {
              //         "AnswerId": 398903,
              //         "AnswerCode": "A6_1",
              //         "AnswerOrder": 7,
              //         "AnswerLabel": "Very Satisfied",
              //         "AnswerType": "Normal",
              //         "AnswerLength": 0,
              //         "Expression": "A6_1=7",
              //         "showIf": null,
              //         "hideIf": null
              //       },
              //       {
              //         "AnswerId": 398903,
              //         "AnswerCode": "A6_1",
              //         "AnswerOrder": 8,
              //         "AnswerLabel": "Don't Know / Not Sure",
              //         "AnswerType": "Normal",
              //         "AnswerLength": 0,
              //         "Expression": "A6_1=8",
              //         "showIf": null,
              //         "hideIf": null
              //       }
              //     ],
              //     "showIf": null,
              //     "hideIf": null,
              //     "PageNo": 1,
              //     "QuestionNote": null
              //   },
              //   {
              //     "QuestionNo": 6,
              //     "QuestionCode": "q_6",
              //     "QuestionType": "MatrixRange",
              //     "QuestionId": 49113,
              //     "MatrixQuestionContext": null,
              //     "QuestionHead": "<span class=\"text-bold\">Please rate your level of agreement with the Intel agent:</span>",
              //     "QuestionName": "Knowledgeable",
              //     "MinAnswer": 4,
              //     "MaxAnswer": 4,
              //     "Answers": [
              //       {
              //         "AnswerId": 398904,
              //         "AnswerCode": "A6_2",
              //         "AnswerOrder": 1,
              //         "AnswerLabel": "Very Dissatisfied",
              //         "AnswerType": "Normal",
              //         "AnswerLength": 0,
              //         "Expression": "A6_2=1",
              //         "showIf": null,
              //         "hideIf": null
              //       },
              //       {
              //         "AnswerId": 0,
              //         "AnswerCode": "A6_2",
              //         "AnswerOrder": 2,
              //         "AnswerLabel": "2",
              //         "AnswerType": "Normal",
              //         "AnswerLength": 0,
              //         "Expression": "A6_2=2",
              //         "showIf": null,
              //         "hideIf": null
              //       },
              //       {
              //         "AnswerId": 0,
              //         "AnswerCode": "A6_2",
              //         "AnswerOrder": 3,
              //         "AnswerLabel": "3",
              //         "AnswerType": "Normal",
              //         "AnswerLength": 0,
              //         "Expression": "A6_2=3",
              //         "showIf": null,
              //         "hideIf": null
              //       },
              //       {
              //         "AnswerId": 398904,
              //         "AnswerCode": "A6_2",
              //         "AnswerOrder": 4,
              //         "AnswerLabel": "Neutral",
              //         "AnswerType": "Normal",
              //         "AnswerLength": 0,
              //         "Expression": "A6_2=4",
              //         "showIf": null,
              //         "hideIf": null
              //       },
              //       {
              //         "AnswerId": 0,
              //         "AnswerCode": "A6_2",
              //         "AnswerOrder": 5,
              //         "AnswerLabel": "5",
              //         "AnswerType": "Normal",
              //         "AnswerLength": 0,
              //         "Expression": "A6_2=5",
              //         "showIf": null,
              //         "hideIf": null
              //       },
              //       {
              //         "AnswerId": 0,
              //         "AnswerCode": "A6_2",
              //         "AnswerOrder": 6,
              //         "AnswerLabel": "6",
              //         "AnswerType": "Normal",
              //         "AnswerLength": 0,
              //         "Expression": "A6_2=6",
              //         "showIf": null,
              //         "hideIf": null
              //       },
              //       {
              //         "AnswerId": 398904,
              //         "AnswerCode": "A6_2",
              //         "AnswerOrder": 7,
              //         "AnswerLabel": "Very Satisfied",
              //         "AnswerType": "Normal",
              //         "AnswerLength": 0,
              //         "Expression": "A6_2=7",
              //         "showIf": null,
              //         "hideIf": null
              //       },
              //       {
              //         "AnswerId": 398904,
              //         "AnswerCode": "A6_2",
              //         "AnswerOrder": 8,
              //         "AnswerLabel": "Don't Know / Not Sure",
              //         "AnswerType": "Normal",
              //         "AnswerLength": 0,
              //         "Expression": "A6_2=8",
              //         "showIf": null,
              //         "hideIf": null
              //       }
              //     ],
              //     "showIf": null,
              //     "hideIf": null,
              //     "PageNo": 1,
              //     "QuestionNote": null
              //   },
              //   {
              //     "QuestionNo": 6,
              //     "QuestionCode": "q_6",
              //     "QuestionType": "MatrixRange",
              //     "QuestionId": 49113,
              //     "MatrixQuestionContext": null,
              //     "QuestionHead": "<span class=\"text-bold\">Please rate your level of agreement with the Intel agent:</span>",
              //     "QuestionName": "Attentiveness",
              //     "MinAnswer": 4,
              //     "MaxAnswer": 4,
              //     "Answers": [
              //       {
              //         "AnswerId": 398905,
              //         "AnswerCode": "A6_3",
              //         "AnswerOrder": 1,
              //         "AnswerLabel": "Very Dissatisfied",
              //         "AnswerType": "Normal",
              //         "AnswerLength": 0,
              //         "Expression": "A6_3=1",
              //         "showIf": null,
              //         "hideIf": null
              //       },
              //       {
              //         "AnswerId": 0,
              //         "AnswerCode": "A6_3",
              //         "AnswerOrder": 2,
              //         "AnswerLabel": "2",
              //         "AnswerType": "Normal",
              //         "AnswerLength": 0,
              //         "Expression": "A6_3=2",
              //         "showIf": null,
              //         "hideIf": null
              //       },
              //       {
              //         "AnswerId": 0,
              //         "AnswerCode": "A6_3",
              //         "AnswerOrder": 3,
              //         "AnswerLabel": "3",
              //         "AnswerType": "Normal",
              //         "AnswerLength": 0,
              //         "Expression": "A6_3=3",
              //         "showIf": null,
              //         "hideIf": null
              //       },
              //       {
              //         "AnswerId": 398905,
              //         "AnswerCode": "A6_3",
              //         "AnswerOrder": 4,
              //         "AnswerLabel": "Neutral",
              //         "AnswerType": "Normal",
              //         "AnswerLength": 0,
              //         "Expression": "A6_3=4",
              //         "showIf": null,
              //         "hideIf": null
              //       },
              //       {
              //         "AnswerId": 0,
              //         "AnswerCode": "A6_3",
              //         "AnswerOrder": 5,
              //         "AnswerLabel": "5",
              //         "AnswerType": "Normal",
              //         "AnswerLength": 0,
              //         "Expression": "A6_3=5",
              //         "showIf": null,
              //         "hideIf": null
              //       },
              //       {
              //         "AnswerId": 0,
              //         "AnswerCode": "A6_3",
              //         "AnswerOrder": 6,
              //         "AnswerLabel": "6",
              //         "AnswerType": "Normal",
              //         "AnswerLength": 0,
              //         "Expression": "A6_3=6",
              //         "showIf": null,
              //         "hideIf": null
              //       },
              //       {
              //         "AnswerId": 398905,
              //         "AnswerCode": "A6_3",
              //         "AnswerOrder": 7,
              //         "AnswerLabel": "Very Satisfied",
              //         "AnswerType": "Normal",
              //         "AnswerLength": 0,
              //         "Expression": "A6_3=7",
              //         "showIf": null,
              //         "hideIf": null
              //       },
              //       {
              //         "AnswerId": 398905,
              //         "AnswerCode": "A6_3",
              //         "AnswerOrder": 8,
              //         "AnswerLabel": "Don't Know / Not Sure",
              //         "AnswerType": "Normal",
              //         "AnswerLength": 0,
              //         "Expression": "A6_3=8",
              //         "showIf": null,
              //         "hideIf": null
              //       }
              //     ],
              //     "showIf": null,
              //     "hideIf": null,
              //     "PageNo": 1,
              //     "QuestionNote": null
              //   },
              //   {
              //     "QuestionNo": 6,
              //     "QuestionCode": "q_6",
              //     "QuestionType": "MatrixRange",
              //     "QuestionId": 49113,
              //     "MatrixQuestionContext": null,
              //     "QuestionHead": "<span class=\"text-bold\">Please rate your level of agreement with the Intel agent:</span>",
              //     "QuestionName": "Understandable",
              //     "MinAnswer": 4,
              //     "MaxAnswer": 4,
              //     "Answers": [
              //       {
              //         "AnswerId": 398906,
              //         "AnswerCode": "A6_4",
              //         "AnswerOrder": 1,
              //         "AnswerLabel": "Very Dissatisfied",
              //         "AnswerType": "Normal",
              //         "AnswerLength": 0,
              //         "Expression": "A6_4=1",
              //         "showIf": null,
              //         "hideIf": null
              //       },
              //       {
              //         "AnswerId": 0,
              //         "AnswerCode": "A6_4",
              //         "AnswerOrder": 2,
              //         "AnswerLabel": "2",
              //         "AnswerType": "Normal",
              //         "AnswerLength": 0,
              //         "Expression": "A6_4=2",
              //         "showIf": null,
              //         "hideIf": null
              //       },
              //       {
              //         "AnswerId": 0,
              //         "AnswerCode": "A6_4",
              //         "AnswerOrder": 3,
              //         "AnswerLabel": "3",
              //         "AnswerType": "Normal",
              //         "AnswerLength": 0,
              //         "Expression": "A6_4=3",
              //         "showIf": null,
              //         "hideIf": null
              //       },
              //       {
              //         "AnswerId": 398906,
              //         "AnswerCode": "A6_4",
              //         "AnswerOrder": 4,
              //         "AnswerLabel": "Neutral",
              //         "AnswerType": "Normal",
              //         "AnswerLength": 0,
              //         "Expression": "A6_4=4",
              //         "showIf": null,
              //         "hideIf": null
              //       },
              //       {
              //         "AnswerId": 0,
              //         "AnswerCode": "A6_4",
              //         "AnswerOrder": 5,
              //         "AnswerLabel": "5",
              //         "AnswerType": "Normal",
              //         "AnswerLength": 0,
              //         "Expression": "A6_4=5",
              //         "showIf": null,
              //         "hideIf": null
              //       },
              //       {
              //         "AnswerId": 0,
              //         "AnswerCode": "A6_4",
              //         "AnswerOrder": 6,
              //         "AnswerLabel": "6",
              //         "AnswerType": "Normal",
              //         "AnswerLength": 0,
              //         "Expression": "A6_4=6",
              //         "showIf": null,
              //         "hideIf": null
              //       },
              //       {
              //         "AnswerId": 398906,
              //         "AnswerCode": "A6_4",
              //         "AnswerOrder": 7,
              //         "AnswerLabel": "Very Satisfied",
              //         "AnswerType": "Normal",
              //         "AnswerLength": 0,
              //         "Expression": "A6_4=7",
              //         "showIf": null,
              //         "hideIf": null
              //       },
              //       {
              //         "AnswerId": 398906,
              //         "AnswerCode": "A6_4",
              //         "AnswerOrder": 8,
              //         "AnswerLabel": "Don't Know / Not Sure",
              //         "AnswerType": "Normal",
              //         "AnswerLength": 0,
              //         "Expression": "A6_4=8",
              //         "showIf": null,
              //         "hideIf": null
              //       }
              //     ],
              //     "showIf": null,
              //     "hideIf": null,
              //     "PageNo": 1,
              //     "QuestionNote": null
              //   }
              // ]

              //text area
              // filteredData[" English"].QuestionModel = [
              //   {
              //     "QuestionNo": 5,
              //     "QuestionCode": "q_5",
              //     "QuestionType": "Open",
              //     "QuestionId": 47918,
              //     "MatrixQuestionContext": null,
              //     "QuestionHead": null,
              //     "QuestionName": "<span class=\"text-bold\">Do you have any comments?</span>",
              //     "MinAnswer": 1,
              //     "MaxAnswer": 1,
              //     "Answers": [
              //       {
              //         "AnswerId": 388768,
              //         "AnswerCode": "A5_1",
              //         "AnswerOrder": 1,
              //         "AnswerLabel": "Comment 1:",
              //         "AnswerType": "TBL",
              //         "AnswerLength": 100,
              //         "Expression": "A5_1=$Input$",
              //         "showIf": null,
              //         "hideIf": null
              //       },
              //       {
              //         "AnswerId": 388769,
              //         "AnswerCode": "A5_2",
              //         "AnswerOrder": 2,
              //         "AnswerLabel": "Comment 2:",
              //         "AnswerType": "TBL",
              //         "AnswerLength": 100,
              //         "Expression": "A5_2=$Input$",
              //         "showIf": null,
              //         "hideIf": null
              //       },
              //       {
              //         "AnswerId": 388770,
              //         "AnswerCode": "A5_3",
              //         "AnswerOrder": 3,
              //         "AnswerLabel": "Comment 3:",
              //         "AnswerType": "TBL",
              //         "AnswerLength": 100,
              //         "Expression": "A5_3=$Input$",
              //         "showIf": null,
              //         "hideIf": null
              //       }
              //     ],
              //     "showIf": null,
              //     "hideIf": null,
              //     "PageNo": 1,
              //     "QuestionNote": null
              //   }
              // ]

              generateLanguageArray(filteredData);

              setSurveyMetaData(
                Object.entries(filteredData).map((entry) => entry[1])
              );

              let oldPage = data.AnswersSelected.CurrentPage;

              let oldPid = data.AnswersSelected.Pid;

              if (currentUrl.includes("dl") && Object.keys(data).length - 1 > 1) {
                let oldDefaultLanguage = window.location.href
                  .split("dl=")[1]
                  .split("&")[0];
                setDefaultLanguage(oldDefaultLanguage);
              } else {
                window.history.pushState(
                  {},
                  "",
                  currentUrl.replace(/dl=\d+&/g,"")
                )
              }
               
              if (currentUrl.includes("pid") && Object.values(data).map(obj => `${obj.ProjectId}`).includes(window.location.href.split('pid=')[1].split('&')[0]) && Object.keys(data).length - 1 > 1) {
                let urlPid = window.location.href
                  .split("pid=")[1]
                  .split("&")[0];
                sessionStorage.setItem("pid", urlPid);
                oldPid = urlPid;
              } else if (oldPid) {
                sessionStorage.setItem("pid", oldPid);
                window.history.pushState(
                  {},
                  "",
                  currentUrl.includes("?")
                    ? `${currentUrl.replace(/&pid=\d+/g,'').replace(/pid=\d+/g,'')}&pid=${oldPid}`
                    : `${currentUrl.replace(/pid=\d+/g,'')}?pid=${oldPid}`
                );
              } else {
                window.history.pushState(
                  {},
                  "",
                  currentUrl.replace(/pid=\d+/,"")
                )                
              }

              if (currentUrl.includes("dl") && Object.keys(data).length - 1 > 1) {
                let oldDefaultLanguage = window.location.href
                  .split("dl=")[1]
                  .split("&")[0];
                setDefaultLanguage(oldDefaultLanguage);
              } else {
                window.history.pushState(
                  {},
                  "",
                  currentUrl.replace(/dl=\d+&/g,"")
                )
              }

              let filteredAnswersSelected;

              if (data.AnswersSelected.AnswersArray) {
                filteredAnswersSelected =
                  data.AnswersSelected.AnswersArray.filter(
                    (answer) => answer
                  ).map((arr) => arr.filter((ans) => ans));
                filteredAnswersSelected = filteredAnswersSelected.map(
                  (answersArray) =>
                    answersArray.filter(
                      (answer) =>
                        !answer.includes("null") 
                    )
                );

                 
                 

                //new code

                  let startingCount = 1;

                  data[Object.keys(data)[0]].QuestionModel.forEach((question) => {
                    if (Number.parseInt(question.PageNo) === startingCount) {
                      paginatedDataCopy[startingCount - 1].push(question);
                    } else {
                      paginatedDataCopy.push([question]);
                      startingCount++;
                    }
                  });

                  let emailTypeQuestionsCopy = {};
                  let numberTypeQuestionsCopy = {};
                  let floatTypeQuestionsCopy = {};

                  paginatedDataCopy.forEach((page) => {
                    page.forEach((question) =>
                      question.Answers.forEach((answer) => {
                        if (answer.AnswerType === "Email") {
                          emailTypeQuestionsCopy[
                            `${answer.Expression.split("=")[0]}_${
                              answer.Expression.split("=")[1]
                            }`
                          ] = question.QuestionCode;
                        }
                      })
                    );
                  });

                  paginatedDataCopy.forEach((page) => {
                    page.forEach((question) =>
                      question.Answers.forEach((answer) => {
                        if (answer.AnswerType === "Numeric") {
                          numberTypeQuestionsCopy[
                            `${answer.Expression.split("=")[0]}_${
                              answer.Expression.split("=")[1]
                            }`
                          ] = question.QuestionCode;
                        }
                      })
                    );
                  });

                  paginatedDataCopy.forEach((page) => {
                    page.forEach((question) =>
                      question.Answers.forEach((answer) => {
                        if (answer.AnswerType === "FloatingPoint") {
                          floatTypeQuestionsCopy[
                            `${answer.Expression.split("=")[0]}_${
                              answer.Expression.split("=")[1]
                            }`
                          ] = question.QuestionCode;
                        }
                      })
                    );
                  });
              
                let paginated = paginatedDataCopy.map(obj => `A${obj[0].QuestionCode.split('_')[1]}`)

                let insertBlank = (lastPage, arr, index, oldValue = null, residualValueArray = []) => {
                    if (index <= lastPage) {
                        if (!arr[index] || (arr[index][0]?.split('=')[0] !== paginated[index]) && (arr[index][0]?.split('_')[0] !== paginated[index])) {
                            let newOldValue = null
                            if (!oldValue) {
                                newOldValue = arr[index] 
                            }
                            let foundIndex = residualValueArray.findIndex(residualValue => (residualValue[0]?.split('=')[0] === paginated[index]) || (residualValue[0]?.split('_')[0] === paginated[index]))

                            arr[index] = residualValueArray.length && foundIndex >= 0 ? residualValueArray[foundIndex] : []
                            residualValueArray.push(arr[index+1])
                             
                            // arr[index+1] = oldValue || newOldValue
                            residualValueArray.push(newOldValue)
                             
                            insertBlank(lastPage, arr, index+1, null, residualValueArray)
                        } else {
                            insertBlank(lastPage, arr, index+1, null, residualValueArray)
                        }
                    } else {
                        return arr
                    }
                }

                // console.log(insertBlank(paginated.indexOf(filteredAnswersSelected[filteredAnswersSelected.length - 1] ? filteredAnswersSelected[filteredAnswersSelected.length - 1][0].split('=')[0] : 1), filteredAnswersSelected, 0, null, [])) 
                      
                setAnsweredQuestions([...filteredAnswersSelected, []]);

                // [
                //   ['A1=_'],
                //   ['A2=10'],
                //   ['A3=3'],
                //   ['A4=27', 'A4_o=-998'],
                //   ['A6=3'],
                //   ['A5=5'],
                //   ['A7=3'],
                //   ['A8=7', 'A8_o=-998'],
                //   [],
                //   ['A10=_'],
                //   ['A11=3'],
                //   ['A12=4'],
                //   ['A13_1=0', 'A13_2=1', 'A13_3=0', 'A13_4=1', 'A13_5=1', 'A13_6=0', 'A13_7=0'],
                //   ['A14=_'],
                //   ['A15_1=0', 'A15_2=0', 'A15_3=1', 'A15_4=0', 'A15_5=1', 'A15_6=0', 'A15_7=0', 'A15_8=0', 'A15_9=0', 'A15_9_o=-998'],
                //   ['A16=2'],
                //   ['A17_1=sd1', 'A17_2=sd2', 'A17_3=sd3'],
                //   ['A18_1=1', 'A18_2=2', 'A18_3=3', 'A18_4=4', 'A18_5=5', 'A18_6=1', 'A18_7=2', 'A18_8=3', 'A18_9=4', 'A18_10=5', 'A18_11=1'],
                //   ['A19_1=-998', 'A19_2=3', 'A19_3=4', 'A19_4=3', 'A19_5=4', 'A19_6=-998', 'A19_7=4', 'A19_8=3', 'A19_9=3', 'A19_10=4', 'A19_11=-998']    
                // ]
                // setAnsweredQuestions([...filteredAnswersSelected]);
              }

              if (oldPage) {
                let filteredAnswersSelected;

                if (data.AnswersSelected.AnswersArray) {
                  filteredAnswersSelected =
                    data.AnswersSelected.AnswersArray.filter(
                      (answer) => answer
                    ).map((arr) => arr.filter((ans) => ans));
                  filteredAnswersSelected = filteredAnswersSelected.map(
                    (answersArray) =>
                      answersArray.filter(
                        (answer) =>
                          !answer.includes("null") 
                      )
                  );
                }

                filteredAnswersSelected = [...filteredAnswersSelected, []]

                const parseDynamicQuestionText = (phrase, skipHtml = false) => {
                  if (phrase) {
                    let parsedText = phrase
                    let matchedQuestionsArray = parsedText.match(/<Q(\d+)>/g)
                    let answerExpressions = []
                    let answerLabels = []
              
                          
                
                    if (matchedQuestionsArray) {
                      matchedQuestionsArray.forEach((questionNumber) => {      
                        let toPush = filteredAnswersSelected.filter(ansArray => ansArray).find(ansArray => ansArray.find(ans => typeof ans === 'string' && ans?.split('=')[0] === `A${questionNumber.split('<Q')[1].split('>')[0]}`))
                        if (toPush) {
                          answerExpressions.push(toPush[0])
                        }
                      })
                
                          
                
                      answerExpressions.forEach((answerExpression) => {
                        let answerNumber = answerExpression.match(/A(\d+)=/)[1]
                         
                        if (answerNumber) {
                          answerLabels.push(paginatedDataCopy?.find(page => page[0].QuestionNo === Number.parseInt(answerNumber))[0].Answers.find(answer => answer.Expression === answerExpression).AnswerLabel)
                        }
                      })
                
                      matchedQuestionsArray.forEach((questionNumber, i) => {
                        parsedText = parsedText.replace(new RegExp(questionNumber, 'g'), answerLabels[i])
                      })
                    }
                     
                    if (skipHtml) {
                            
                      return parsedText
                    } else {
                            
                      return parsedText.replace(/<[^>]*>/g, "")
                    }
                  } else {
                    return null
                  }
                }

                const showQuestion = (showIfLogic, questionObj) => {
                  let result;
                  if (showIfLogic) {

                    let showIfLogicParsed = showIfLogic?.replace("<>", "!==")?.replace(/</g, "")?.replace(/>/g, "");

                    let showIfLogicArray = showIfLogicParsed.split(/\|\||&&/).map(ans => ans.replace("((","").replace("))","").replace("(","").replace(")","").replace("<","").replace(">",""))

                    showIfLogicArray.forEach(showIfLogicPiece => {
                      if (!showIfLogicPiece.includes("!==")) {
                        if (
                          filteredAnswersSelected.find((answerArray) => answerArray?.includes(showIfLogicPiece.replace('==', '')))
                        ) {
                          showIfLogicParsed = showIfLogicParsed.replace(
                            eval("/" + showIfLogicPiece + "/g"),
                            "true"
                          );
                        } else {
                          showIfLogicParsed = showIfLogicParsed.replace(
                            eval("/" + showIfLogicPiece + "/g"),
                            "false"
                          );
                        }
                      } else {
                        if (
                          filteredAnswersSelected.find((answerArray) => answerArray?.includes(showIfLogicPiece.replace('!=', ''))
                        )) {
                          showIfLogicParsed = showIfLogicParsed.replace(
                            eval("/" + showIfLogicPiece + "/g"),
                            "false"
                          );
                        } else {
                          showIfLogicParsed = showIfLogicParsed.replace(
                            eval("/" + showIfLogicPiece + "/g"),
                            "true"
                          );
                        }          
                      }
                  })

                     
                    if (questionObj?.QuestionType === "MatrixLabel" && questionObj) {
                      result = eval(showIfLogicParsed) && questionObj.Answers?.some((answerObj) => {
                        return (answerObj?.showIf
                          ? showQuestion(answerObj.showIf)
                          : true);
                      }) && parseDynamicQuestionText(questionObj.QuestionName) !== "undefined";
                    } else if (questionObj) {
                      result = eval(showIfLogicParsed) && questionObj.Answers?.some((answerObj) => {
                        return (answerObj?.showIf
                          ? showQuestion(answerObj.showIf)
                          : true);
                      });
                    } else {
                      result = eval(showIfLogicParsed)
                    }
                  } else if (questionObj) {
                    result = questionObj.Answers?.some((answerObj) => {
                      return (answerObj?.showIf
                        ? showQuestion(answerObj.showIf)
                        : true);
                    });
                  } else {
                    result = true;
                  }

                  // Q18: skip when user selected exactly one A17 option (any of A17_1..A17_7)
                  if (questionObj?.QuestionCode === "q_18") {
                    const a17Array = filteredAnswersSelected.find(
                      (arr) => arr && arr.some((s) => typeof s === "string" && s.startsWith("A17_"))
                    );
                    if (a17Array) {
                      const selectedCount = a17Array.filter(
                        (s) => typeof s === "string" && s.startsWith("A17_") && s.includes("=1")
                      ).length;
                      if (selectedCount === 1) {
                        return false;
                      }
                    }
                  }

                  return result;
                };

                let startingCount = 1;

                Object.entries(filteredData).map((entry) => entry[1]).find((metaData) => metaData.ProjectId === Number.parseInt(oldPid))?.QuestionModel?.forEach((question) => {
                  if (Number.parseInt(question.PageNo) === startingCount) {
                    paginatedDataCopy[startingCount - 1].push(question);
                  } else {
                    paginatedDataCopy.push([question]);
                    startingCount++;
                  }
                });

                if (filteredAnswersSelected.length === 0) {
                  let blankArray = [];
                  paginatedDataCopy.forEach((arr) => blankArray.push([]));
                         
                  setAnsweredQuestions(blankArray);
                }

                let nextPage = oldPage - 1;

                const checkNext = (pageToCheck) => {
                   
                  const nextPageQuestions = paginatedDataCopy[pageToCheck + 1];
                          
                  if (
                    nextPageQuestions?.every((questionObj) => {
                      return !(showQuestion(questionObj.showIf, questionObj));
                    })
                    ) {
                              
                      nextPage += 1;
                      checkNext(pageToCheck + 1);
                    }
                };

                  
                checkNext(oldPage - 1);

                window.shown = 0;
                setCurrentPageAnswers([]);
                                      
                setCurrentPage(nextPage + 2);
              }

              setChosenLanguage(oldPid);


              if ((Object.keys(data).length - 1 === 1) && !window.sessionStorage.pid) {
                //if just one language exists, we do the next step
                confirmLanguage(Object.values(data)[0].ProjectId, setMessageError, setAttention, clearStorage, loaderRef, setFetchError, setChosenLanguage)

                const newUrl = currentUrl.includes("?")
                ? `${window.location.href.replace(/&pid=\d+/g,'').replace(/pid=\d+/g,'')}&pid=${Object.values(data)[0].ProjectId}`
                : `${window.location.href.replace(/pid=\d+/g,'')}?pid=${Object.values(data)[0].ProjectId}`;
                window.history.pushState({}, "", newUrl);

                loaderRef.current.complete();
              } else if (/pid=\d+/.test(window.location.href)) {
                //if there's a pid in the url, we do the next step
                if (Object.values(data).map(obj => `${obj.ProjectId}`).includes(window.location.href.split('pid=')[1].split('&')[0])) {
                  confirmLanguage(window.location.href.split('pid=')[1].split('&')[0], setMessageError, setAttention, clearStorage, loaderRef, setFetchError, setChosenLanguage)
                }
                loaderRef.current.complete();
              }

            } else {
                  
              setFetchError(true);
              loaderRef.current.complete();
            }
          })
          .catch((err) => {
                
            setFetchError(true);
            loaderRef?.current?.complete();
          });
      };

      let resumeCheck = () => {
        if (sessionStorage.getItem("tidInitial")) {
          fetchData();
        } else {
          sessionStorage.setItem("tidInitial", new Date().toJSON());
          setTimeout(() => {
            resumeCheck();
          }, 500);
        }
      };

      resumeCheck();
    } else {
          
      setFetchError(true);
    }
  }, []);

  return (
    <DataContext.Provider
      value={{
        surveyMetaData,
        surveyData,
        setSurveyData,
        paginatedData,
        defaultLanguage,
        setDefaultLanguage,
        englishCode,
        languageArray,
        chosenLanguage,
        setChosenLanguage,
        fetchError,
        setFetchError,
        messageError,
        setMessageError,
        attention,
        setAttention,
        currentPage,
        setCurrentPage,
        loaderRef,
        wrapperWidth,
        previousAnswers,
        setPreviousAnswers,
        thankYou,
        setThankYou,
        stateCleanser,
        setStateCleanser
      }}
    >
      {children}
    </DataContext.Provider>
  );
};
